import type { TFunction } from "i18next";
import { ISelectOption } from "components/common/inputs/inputTypes";

type Translate = TFunction | ((key: string) => string);

const label = (t: Translate, key: string | undefined, fallback?: string) =>
  key ? t(key) : fallback ?? "";

/** PASS_TRANSACTION_STATUS display (API numeric IDs unchanged). */
export const PASS_TRANSACTION_STATUS_KEYS: Record<number, string> = {
  0: "shop.paid",
  1: "shop.awaitingPayment",
  2: "shop.debtCollection",
  3: "shop.unpaid",
  4: "shop.overdue",
  5: "shop.cancelled",
  6: "shop.vacation",
};

export const getPassTransactionStatusLabel = (t: Translate, status: number): string =>
  label(t, PASS_TRANSACTION_STATUS_KEYS[status], String(status));

/** PASS_PAYMENT_METHOD display. */
export const PASS_PAYMENT_METHOD_KEYS: Record<number, string> = {
  0: "shop.cash",
  1: "shop.bankTransfer",
};

export const getPassPaymentMethodLabel = (t: Translate, method: number): string =>
  label(t, PASS_PAYMENT_METHOD_KEYS[method], String(method));

/** PAYMENT_METHOD (Gotówka / JustWin / Przelew) display. */
export const PAYMENT_METHOD_KEYS: Record<number, string> = {
  0: "shop.cash",
  1: "shop.justWin",
  2: "shop.bankTransfer",
};

export const getPaymentMethodLabel = (t: Translate, method: number): string =>
  label(t, PAYMENT_METHOD_KEYS[method], String(method));

/** PLAYER_STATE display. */
export const PLAYER_STATE_KEYS: Record<number, string> = {
  0: "players.stateObservationRequestReceived",
  1: "players.stateObservationRequestSent",
  2: "players.stateMembershipRequestReceived",
  3: "players.stateMembershipRequestSent",
  4: "players.stateObserved",
  5: "players.stateReportedShort",
  6: "players.stateRejected",
  7: "players.stateFormerMember",
  8: "players.stateObservedInPast",
  9: "players.stateObservedFormerMember",
  10: "players.stateLimitedAccess",
  11: "players.stateMemberSuspended",
  12: "players.stateActive",
  13: "players.stateTerminating",
  15: "players.stateToDelete",
};

export const getPlayerStateLabel = (t: Translate, state: number): string =>
  label(t, PLAYER_STATE_KEYS[state], String(state));

/** PLAYER_STATE_CHANGE display (select / toast). */
export const PLAYER_STATE_CHANGE_KEYS: Record<number, string> = {
  0: "players.changeAccept",
  1: "players.restrictAccess",
  2: "players.suspend",
  3: "players.expel",
  4: "players.revoke",
  5: "players.requestMembership",
  6: "players.stateActive",
  7: "players.transfer",
  8: "players.refuseResignation",
  9: "common.cancel",
  10: "players.stateToDelete",
};

export const getPlayerStateChangeLabel = (t: Translate, id: number): string =>
  label(t, PLAYER_STATE_CHANGE_KEYS[id], String(id));

/** GENDER display. */
export const GENDER_KEYS: Record<number, string> = {
  0: "players.genderMale",
  1: "players.genderFemale",
};

export const getGenderLabel = (t: Translate, gender: number): string =>
  label(t, GENDER_KEYS[gender], String(gender));

/** PREFERRED_POSITION display. */
export const PREFERRED_POSITION_KEYS: Record<number, string> = {
  0: "players.positionGoalkeeper",
  1: "players.positionDefender",
  2: "players.positionMidfielder",
  3: "players.positionForward",
};

export const getPreferredPositionLabel = (t: Translate, position: number): string =>
  label(t, PREFERRED_POSITION_KEYS[position], String(position));

/** DOMINANT_LEG display. */
export const DOMINANT_LEG_KEYS: Record<number, string> = {
  0: "players.legRight",
  1: "players.legLeft",
  2: "players.legBoth",
};

export const getDominantLegLabel = (t: Translate, leg: number): string =>
  label(t, DOMINANT_LEG_KEYS[leg], String(leg));

/** ORDER_HISTORY_STATE display. */
export const ORDER_HISTORY_STATE_KEYS: Record<number, string> = {
  0: "shop.paidPlural",
  1: "shop.awaitingPayment",
  2: "shop.orderProcessing",
  3: "shop.inDelivery",
  4: "shop.completed",
};

export const getOrderHistoryStateLabel = (t: Translate, state: number): string =>
  label(t, ORDER_HISTORY_STATE_KEYS[state], String(state));

/** PAYMENT_STATUS display. */
export const PAYMENT_STATUS_KEYS: Record<number, string> = {
  0: "shop.awaitingPayment",
  1: "shop.paidPlural",
  2: "shop.paymentFailure",
  3: "shop.verified",
};

export const getPaymentStatusLabel = (t: Translate, status: number): string =>
  label(t, PAYMENT_STATUS_KEYS[status], String(status));

/** ORDER_REFOUND_STATUS display. */
export const ORDER_REFUND_STATUS_KEYS: Record<number, string> = {
  0: "shop.cancelled",
  1: "shop.inDelivery",
  2: "shop.approved",
  3: "shop.processing",
  4: "shop.deliveryFailed",
};

export const getOrderRefundStatusLabel = (t: Translate, status: number): string =>
  label(t, ORDER_REFUND_STATUS_KEYS[status], String(status));

/** TRAINER_STATE display. */
export const TRAINER_STATE_KEYS: Record<number, string> = {
  0: "trainerDetail.stateRequestFromTrainer",
  1: "trainerDetail.stateRequestFromAcademy",
  2: "trainerDetail.stateRequestCancelled",
  3: "trainerDetail.collaborator",
  4: "trainerDetail.formerCollaborator",
};

export const getTrainerStateLabel = (t: Translate, state: number): string =>
  label(t, TRAINER_STATE_KEYS[state], String(state));

/** BUSINESS_TYPE display (API IDs start at 1). */
export const BUSINESS_TYPE_KEYS: Record<number, string> = {
  1: "system.businessTypeNaturalPerson",
  2: "system.businessTypeSoleProprietorship",
  3: "system.businessTypeCivilPartnership",
  4: "system.businessTypeGeneralPartnership",
  5: "system.businessTypeLimitedPartnership",
  6: "system.businessTypeLimitedJointStockPartnership",
  7: "system.businessTypeJointStockCompany",
  8: "system.businessTypeLimitedLiabilityCompany",
  9: "system.businessTypeAssociationFoundation",
  10: "system.businessTypeCooperative",
};

export const getBusinessTypeLabel = (t: Translate, type: number): string =>
  label(t, BUSINESS_TYPE_KEYS[type], String(type));

/** TRAINER_TYPE display. */
export const TRAINER_TYPE_KEYS: Record<number, string> = {
  0: "club.badgeTrainer",
  1: "club.badgeScout",
  2: "club.badgeManager",
  3: "club.badgeAnalyst",
};

export const getTrainerTypeLabel = (t: Translate, type: number): string =>
  label(t, TRAINER_TYPE_KEYS[type], String(type));

/** LICENSE_TYPE display. */
export const LICENSE_TYPE_KEYS: Record<number, string> = {
  0: "trainers.licenseUefa",
  1: "trainers.licenseUefaPro",
};

export const getLicenseTypeLabel = (t: Translate, type: number): string =>
  label(t, LICENSE_TYPE_KEYS[type], String(type));

/** OBSERVATION_LEVEL display. */
export const OBSERVATION_LEVEL_KEYS: Record<number, string> = {
  0: "players.observationLevelBasic",
  1: "players.observationLevelFull",
};

export const getObservationLevelLabel = (t: Translate, level: number): string =>
  label(t, OBSERVATION_LEVEL_KEYS[level], String(level));

/** PASS_TYPE display. */
export const PASS_TYPE_KEYS: Record<number, string> = {
  0: "system.passTypeUndetermined",
  1: "system.passTypeDetermined",
};

export const getPassTypeLabel = (t: Translate, type: number): string =>
  label(t, PASS_TYPE_KEYS[type], String(type));

/** PASS_PAYMENT_TYPE display. */
export const PASS_PAYMENT_TYPE_KEYS: Record<number, string> = {
  0: "system.paymentMonthly",
  1: "system.paymentInstallments",
  2: "system.paymentOneTime",
};

export const getPassPaymentTypeLabel = (t: Translate, type: number): string =>
  label(t, PASS_PAYMENT_TYPE_KEYS[type], String(type));

/** ACADEMY_TYPE display. */
export const ACADEMY_TYPE_KEYS: Record<number, string> = {
  0: "system.typeAcademy",
  1: "system.typeRepresentation",
  2: "system.typeClub",
};

export const getAcademyTypeLabel = (t: Translate, type: number): string =>
  label(t, ACADEMY_TYPE_KEYS[type], String(type));

/** EVENT_LOCALIZATION_TYPE display. */
export const EVENT_LOCALIZATION_TYPE_KEYS: Record<number, string> = {
  0: "events.locationGym",
  1: "events.locationArtificial",
  2: "events.locationGrass",
  3: "events.locationOther",
};

export const getEventLocalizationTypeLabel = (
  t: Translate,
  type: number,
): string => label(t, EVENT_LOCALIZATION_TYPE_KEYS[type], String(type));

/** PRODUCT_TYPES display. */
export const PRODUCT_TYPE_KEYS: Record<number, string> = {
  0: "shop.physical",
  1: "shop.electronic",
};

export const getProductTypeLabel = (t: Translate, type: number): string =>
  label(t, PRODUCT_TYPE_KEYS[type], String(type));

/** PRODUCT_VERIFICATION_STATUS display. */
export const PRODUCT_VERIFICATION_STATUS_KEYS: Record<number, string> = {
  0: "shop.processing",
  1: "shop.verified",
  2: "shop.cancelled",
};

export const getProductVerificationStatusLabel = (
  t: Translate,
  status: number,
): string => label(t, PRODUCT_VERIFICATION_STATUS_KEYS[status], String(status));

/** PRODUCT_ACTIVATION_STATUS display (API IDs start at 1). */
export const PRODUCT_ACTIVATION_STATUS_KEYS: Record<number, string> = {
  1: "shop.active",
  2: "shop.inactive",
};

export const getProductActivationStatusLabel = (
  t: Translate,
  status: number,
): string => label(t, PRODUCT_ACTIVATION_STATUS_KEYS[status], String(status));

/** ACTIVITY_TYPE short codes (T/TA/TP/TO/MP) + unknown fallback. */
const ACTIVITY_TYPE_CODES: Record<number, string> = {
  0: "T",
  1: "TA",
  2: "TP",
  3: "TO",
  4: "MP",
};

export const getActivityTypeLabel = (t: Translate, type: number): string =>
  ACTIVITY_TYPE_CODES[type] ?? t("events.unknown");

/** LIST_SORT select options (numeric values unchanged). */
export const getListSortOptions = (t: Translate): Array<ISelectOption<number>> => [
  { label: t("events.sortNewest"), value: 0 },
  { label: t("events.sortOldest"), value: 1 },
  { label: t("events.sortNameAsc"), value: 2 },
  { label: t("events.sortNameDesc"), value: 3 },
];

/** PASSES_SORT_LIST select options. */
export const getPassesSortOptions = (t: Translate): Array<ISelectOption<number>> => [
  { label: t("events.sortNewest"), value: 0 },
  { label: t("events.sortOldest"), value: 1 },
];

/** ORDER_SORT_LIST select options. */
export const getOrderSortOptions = (t: Translate): Array<ISelectOption<number>> => [
  { label: t("events.sortNewest"), value: 0 },
  { label: t("events.sortOldest"), value: 1 },
  { label: t("shop.sortValueAsc"), value: 2 },
  { label: t("shop.sortValueDesc"), value: 3 },
];
